import Image, { ImageProps, StaticImageData } from "next/image";
import Link from "next/link";
import Meta from "@/components/meta";
import Navbar from "@/components/navbar";
import ArrowRight from "@/components/icons/arrow_right";
import Footer from "@/components/footer";
import imgTile from "@/components/climate-leads/tile.png";
import imgEarth from "@/components/climate-leads/textured-earth-full.webp";

import InterviewAmeet from "@/components/climate-leads/interview_ameet.mdx";
import InterviewBen from "@/components/climate-leads/interview_ben.mdx";
import InterviewEbba from "@/components/climate-leads/interview_ebba.mdx";
import InterviewKim from "@/components/climate-leads/interview_kim.mdx";
import InterviewMia from "@/components/climate-leads/interview_mia.mdx";
import InterviewWill from "@/components/climate-leads/interview_will.mdx";

import avatarAmeet from "@/components/climate-leads/avatar-ameet.webp";
import avatarBen from "@/components/climate-leads/avatar-ben.webp";
import avatarEbba from "@/components/climate-leads/avatar-ebba.webp";
import avatarKim from "@/components/climate-leads/avatar-kim.webp";
import avatarMia from "@/components/climate-leads/avatar-mia.webp";
import avatarWill from "@/components/climate-leads/avatar-will.webp";

import iconAirbnb from "@/components/climate-leads/icon-airbnb.svg";
import iconAledade from "@/components/climate-leads/icon-aledade.svg";
import iconBoom from "@/components/climate-leads/icon-boom.svg";
import iconEverlane from "@/components/climate-leads/icon-everlane.svg";
import iconPinterest from "@/components/climate-leads/icon-pinterest.svg";
import iconSpotify from "@/components/climate-leads/icon-spotify.svg";

const people: Record<
  string,
  {
    avatar: StaticImageData;
    name: string;
    role: string;
    interview: (props: any) => JSX.Element;
  }
> = {
  ameet: {
    avatar: avatarAmeet,
    name: "Ameet Konkar",
    role: "Head of Sustainability",
    interview: InterviewAmeet,
  },
  mia: {
    avatar: avatarMia,
    name: "Mia Ketterling",
    role: "Workplace Sustainability Manager",
    interview: InterviewMia,
  },
  ben: {
    avatar: avatarBen,
    name: "Ben Murphy",
    role: "Head of Sustainability Policy",
    interview: InterviewBen,
  },
  ebba: {
    avatar: avatarEbba,
    name: "Ebba Grythberg",
    role: "Global Sustainability Manager",
    interview: InterviewEbba,
  },
  kim: {
    avatar: avatarKim,
    name: "Kimberley Smith",
    role: "Chief Supply Chain Officer",
    interview: InterviewKim,
  },
  will: {
    avatar: avatarWill,
    name: "Will Palmisano",
    role: "Senior Product Analyst",
    interview: InterviewWill,
  },
};
const companies: Record<
  string,
  { name: string; icon: StaticImageData; person: typeof people[string] }
> = {
  airbnb: {
    name: "Airbnb",
    icon: iconAirbnb,
    person: people.ameet,
  },
  everlane: {
    name: "Everlane",
    icon: iconEverlane,
    person: people.kim,
  },
  boom: {
    name: "Boom Supersonic",
    icon: iconBoom,
    person: people.ben,
  },
  aledade: {
    name: "Aledade",
    icon: iconAledade,
    person: people.will,
  },
  pinterest: {
    name: "Pinterest",
    icon: iconPinterest,
    person: people.mia,
  },
  spotify: {
    name: "Spotify",
    icon: iconSpotify,
    person: people.ebba,
  },
};

function TOC() {
  return (
    <ul className="toc">
      {Object.entries(companies).map(([id, co]) => (
        <li key={id}>
          <a
            href={`#${id}`}
            onClick={(e) => {
              if (
                window.matchMedia("(prefers-reduced-motion: no-preference)")
                  .matches
              ) {
                e.preventDefault();
                document
                  .querySelector(`#${id}`)
                  .scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            <span>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={co.icon.src} width={75} height={75} alt={co.name} />
            </span>
            <Image
              src={co.person.avatar.src}
              width={75}
              height={75}
              alt={co.person.name}
            />
          </a>
        </li>
      ))}
      <style jsx>{`
        ul {
          list-style: none;
          display: flex;
          justify-content: center;
          gap: 10px;
          padding: 0;
        }
        li {
          margin: 0;
          background-color: #013aad;
          width: 75px;
          height: 75px;
          overflow: hidden;
          border-radius: 75px;
          line-height: 0;
        }
        img {
          max-width: 100%;
          max-height: 100%;
        }
        a {
          display: grid;
        }
        a > :global(span) {
          border-radius: 75px;
          width: 100%;
          height: 100%;
        }
        a > :global(span:last-child) {
          display: none !important;
        }
        @media (hover: hover) and (min-width: 520px) {
          a > :global(span) {
            transition: 0.5s transform cubic-bezier(0.85, 0, 0.15, 1);
          }
          a > :global(span:last-child) {
            display: block !important;
          }
          a:hover > :global(span),
          a:focus > :global(span) {
            transform: translateY(-100%);
          }
        }
        @media (hover: hover) and (prefers-reduced-motion: reduce) {
          a > :global(span) {
            transition-duration: 0s;
          }
        }
        @media (min-width: 520px) {
          ul {
            margin-top: 32px;
          }
        }
        @media (max-width: 520px) {
          ul {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            column-gap: 24px;
            row-gap: 12px;
          }
          li {
            aspect-ratio: 1 / 1;
            display: grid;
            place-content: center;
            width: auto;
            height: auto;
          }
        }
        @media (min-width: 1250px) {
          ul {
            justify-content: flex-start;
          }
        }
      `}</style>
    </ul>
  );
}

function Banner() {
  return (
    <header>
      <div className="wrapper">
        <div>
          <h1>
            Profiles in
            <br />
            Climate Action
          </h1>
        </div>
        <div>
          <p>
            Climate Leads are the internal champions driving decarbonization
            within companies. They do the work behind the scenes to mobilize
            businesses to solve climate change.
          </p>
          <p>
            They’re rethinking their businesses for a zero-carbon world, and
            working across far-flung teams to turn pledges into reality. They’re
            optimistic and grounded, tenacious and resourceful. They’re
            engineers, advocates, educators, and operators.
          </p>
          <p>
            Their work really matters—many manage more carbon than entire
            cities. At Watershed, we’re lucky to support them every day.
          </p>
        </div>
        <div>
          <TOC />
        </div>
      </div>
      <figure>
        <Image
          src={imgEarth}
          alt="Illustration of earth"
          priority
          objectFit="cover"
          objectPosition="center bottom"
        />
      </figure>
      <div className="cut" />
      <style jsx>{`
        header {
          background-color: #4b72e6;
          background-image: url(${imgTile.src});
          background-size: 330px;
          position: relative;
          overflow: hidden;
          margin-top: calc(-1 * var(--nav-height));
        }
        .wrapper {
          color: #fff;
          padding-top: calc(96px + var(--nav-height));
          padding-bottom: 96px;
          display: grid;
          position: relative;
          z-index: 1;
        }
        .wrapper div {
          padding-inline: 24px;
        }
        figure {
          z-index: 0;
          line-height: 0;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
        }
        .cut {
          background-color: #fff;
          width: 100%;
          height: 66px;
          position: absolute;
          bottom: -0.5px; // fix Chrome visual rendering issue
          left: 0;
          right: 0;
          clip-path: polygon(0 0, 100% 100%, 100% 100%, 0% 100%);
        }
        @media (min-width: 520px) and (max-width: 820px) {
          .wrapper {
            padding-bottom: 160px;
          }
        }
        @media (max-width: 820px) {
          figure > :global(span) {
            min-height: 300px;
          }
        }
        @media (min-width: 820px) {
          .wrapper {
            padding: 128px 32px max(256px, 33vh);
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .wrapper div:first-child {
            text-align: right;
          }
          .wrapper div:last-child {
            grid-column: span 2;
          }
          h1 {
            font-size: 64px;
          }
          p {
            max-width: 54ch;
          }
          .cut {
            height: clamp(100px, 18vh, 150px);
          }
        }
        @media (min-width: 1250px) {
          .wrapper {
            padding-bottom: max(384px, 25vw);
          }
          .wrapper div:last-child {
            grid-column: 2;
          }
        }
        h1 {
          font-family: var(--font-family-serif);
          line-height: 1;
        }
        p {
          color: inherit;
          font-size: 18px;
          line-height: 24px;
          font-weight: 600;
          margin-bottom: 24px;
        }
      `}</style>
    </header>
  );
}

function Avatar({ src, alt }: Pick<ImageProps, "src" | "alt">) {
  return (
    <>
      <Image
        src={src}
        alt={alt}
        width={160}
        height={160}
        placeholder="blur"
        className="avatar"
        layout="fixed"
      />
      <style jsx>{`
        :global(.avatar) {
          border-radius: 9999px;
        }
      `}</style>
    </>
  );
}

function NameAndTitle({
  name,
  title,
  company,
}: {
  name: string;
  title: string;
  company: string;
}) {
  return (
    <div>
      <h2>{name}</h2>
      <p>
        {title}
        <span>, {company}</span>
      </p>
      <style jsx>{`
        div {
          padding-bottom: 16px;
        }
        h2 {
          color: #000;
          font-family: var(--font-family-serif);
          font-size: 32px;
          margin-bottom: 0;
        }
        p {
          font-family: var(--font-family);
          font-size: 18px;
          color: var(--color-medium-cobalt);
          font-weight: 600;
          margin-bottom: 0;
        }
        @media (min-width: 820px) {
          span {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

function Section({ id }: { id?: string }) {
  const { name, person } = companies[id];
  const Interview = person.interview;
  return (
    <section id={id} className="container">
      <header>
        <Avatar src={person.avatar} alt={person.name} />
      </header>
      <Interview
        components={{
          Header: () => (
            <NameAndTitle
              name={person.name}
              title={person.role}
              company={name}
            />
          ),
          a: (props) => (
            <a target="_blank" rel="noreferrer noopener" {...props} />
          ),
        }}
      />
      <style jsx>{`
        section {
          display: grid;
          column-gap: 32px;
          row-gap: 16px;
          margin-block: 32px;
          scroll-margin-top: 48px;
        }
        section:nth-of-type(odd) {
          --color-highlight: #caf5ff;
        }
        section:nth-of-type(even) {
          --color-highlight: #caffe8;
        }
        section:first-of-type {
          margin-top: -32px;
        }
        @media (min-width: 820px) {
          section {
            column-gap: 64px;
            margin-block: 96px;
            scroll-margin-top: 72px;
          }
          section:first-of-type {
            margin-top: -128px;
          }
          section:nth-of-type(odd) {
            grid-template-columns: 3fr 2fr;
          }
          section:nth-of-type(even) {
            grid-template-columns: 2fr 3fr;
          }
          section :global(header),
          section :global(blockquote) {
            grid-column: span 2;
          }
          section:nth-of-type(odd) :global(aside) {
            grid-column: 2;
            grid-row: 2;
          }
          section:nth-of-type(even) :global(header) {
            grid-column: 2;
            grid-row: 1;
          }
          section:nth-of-type(even) :global(aside) {
            grid-column: 1;
            grid-row: 2;
          }
          section:nth-of-type(even) :global(article) {
            grid-column: 2;
          }
        }
        @media (min-width: 1100px) {
          section:first-of-type {
            margin-top: -160px;
          }
        }
        @media (max-width: 820px) {
          section:last-of-type {
            margin-bottom: 0;
          }
        }
      `}</style>
      <style jsx global>{`
        article h3 {
          font-size: 18px;
          margin-bottom: 0;
        }
        article p {
          color: #000;
          font-size: 18px;
        }
        aside p:last-child {
          margin-bottom: 0;
        }
        article ~ blockquote p {
          color: var(--color-core-cobalt);
          font-family: var(--font-family-serif);
          font-size: 36px;
          line-height: 1.25;
          max-width: none;
          margin: 0;
        }
        article ~ blockquote mark {
          background-color: var(--color-highlight);
          color: inherit;
        }
        article ~ blockquote p:first-of-type {
          text-indent: -0.7ch;
        }
        article ~ blockquote p:first-of-type::before {
          content: "“";
        }
        article ~ blockquote p:first-of-type::after {
          content: "”";
        }
        @media (min-width: 520px) {
          article ~ blockquote {
            margin-top: 48px;
          }
          article ~ blockquote p {
            font-size: 48px;
            letter-spacing: -0.03em;
            line-height: 1.25;
          }
        }
        @media (max-width: 520px) {
          article ~ blockquote {
            padding-left: 16px;
            padding-right: 8px;
          }
        }
      `}</style>
    </section>
  );
}

function PreviousCampaign() {
  return (
    <div>
      <Link href="/blog/earth-day-2021">
        <a>
          2021 Climate Spotlight
          <ArrowRight />
        </a>
      </Link>
      <style jsx>{`
        div {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-bottom: 48px;
        }
        a {
          display: flex;
          align-items: center;
          gap: 8px;
          text-align: center;
          text-decoration: none;
          background-color: #e0f0ff;
          color: #265cff;
          padding: 16px;
          padding-right: 32px;
          border-radius: 8px 0 0 8px;
          font-size: 16px;
          line-height: 1.375;
          transition: 0.125s background-color ease-in-out;
        }
        a:hover,
        a:focus {
          background-color: #c6ddff;
        }
        a :global(svg) {
          stroke: currentColor;
        }
        @media (min-width: 820px) {
          div {
            margin-top: 48px;
          }
        }
      `}</style>
    </div>
  );
}

export default function EarthDayPage() {
  return (
    <>
      <Meta
        title="Climate Leads: Profiles in Climate Action"
        description="Honoring Climate Leads, the internal champions driving impact within companies."
        image="https://watershed.com/img/climate-leads.png"
      />
      <Navbar sticky translucent linkColor="#fff" />
      <Banner />
      <main>
        {Object.keys(companies).map((co) => (
          <Section key={co} id={co} />
        ))}
      </main>
      <PreviousCampaign />
      <Footer />
      <style jsx>{`
        main {
          padding-inline: 4px;
          padding-block: 0;
        }
        @media (min-width: 520px) {
          main {
            padding-inline: 12px;
          }
        }
        @media (min-width: 820px) {
          main {
            padding-inline: 20px;
          }
        }
      `}</style>
      <style jsx global>{`
        :root {
          --font-family-serif: "Mackinac Pro", ui-serif, Georgia, serif;
          --color-medium-cobalt: #5d93ff;
          --color-core-cobalt: #3366ff;
          --color-deep-cobalt: #0033cc;
          --container-width: 1024px;
        }

        @font-face {
          font-family: "Mackinac Pro";
          src: url("/fonts/P22MackinacProBook.eot");
          src: url("/fonts/P22MackinacProBook.eot?#iefix")
              format("embedded-opentype"),
            url("/fonts/P22MackinacProBook.woff") format("woff"),
            url("/fonts/P22MackinacProBook.svg") format("svg");
          font-weight: normal;
          font-style: normal;
        }

        @font-face {
          font-family: "Mackinac Pro";
          src: url("/fonts/P22MackinacProMedium.eot");
          src: url("/fonts/P22MackinacProMedium.eot?#iefix")
              format("embedded-opentype"),
            url("/fonts/P22MackinacProMedium.woff") format("woff"),
            url("/fonts/P22MackinacProMedium.svg") format("svg");
          font-weight: bold;
          font-style: normal;
        }
      `}</style>
    </>
  );
}
